class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload and the shipping progress. Keep one explicit /cart.js fetch on
  // open so both use the current cart state.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    const progress = this.querySelector('[data-free-shipping-progress]');
    if (!CartViewEvent && !progress) return;

    try {
      const response = await fetch(routes.cart_url + '.js');
      const cart = await response.json();
      this.updateFreeShippingProgress(cart);

      if (!CartViewEvent) return;
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // Keep the server-rendered progress and skip the informational event.
    }
  }

  updateFreeShippingProgress(cart) {
    const progress = this.querySelector('[data-free-shipping-progress]');
    if (!progress || !cart) return;

    const threshold = Number(progress.dataset.threshold);
    const message = progress.querySelector('[data-free-shipping-message]');
    const bar = progress.querySelector('[data-free-shipping-bar]');
    if (!threshold || !message || !bar) return;

    const remaining = Math.max(threshold - cart.total_price, 0);
    const percent = Math.min((cart.total_price / threshold) * 100, 100);
    bar.style.width = percent + '%';
    message.textContent = remaining
      ? 'You’re ' +
        new Intl.NumberFormat(document.documentElement.lang, {
          style: 'currency',
          currency: cart.currency,
        }).format(remaining / 100) +
        ' away from free shipping.'
      : 'Your order is eligible for free delivery! Yay!';
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
